import React, { useMemo, useState } from "react";
import PageTitle from "@cs/components/src/PageTitle";
import Button from "@cs/components/src/Button";
import { AlertTriangle, Plus, RotateCcw, Search } from "react-feather";
import Spinner from "@cs/components/src/Spinner";
import { useMutation, useQuery } from "react-query";
import ApiService from "../../services/ApiService";
import styled from "styled-components";
import { PageWrapper } from "@cs/components/src/PageWrapper";
import { formatNumber } from "../shared/utils";
import Fuse from "fuse.js";
import Input from "@cs/components/src/Input";
import { Link } from "react-router-dom";
import { useOrganizationAuthCheck } from "../../hooks/useOrganizationAuthCheck";
import Tooltip from "@cs/components/src/Tooltip";
import { useUpdateComponentsJob } from "../../context/UpdateComponentsJobContext";
import UsageBarChart from "./UsageBarChart";

/**
 * Organization list (Monday 12676941123) — a uniform card grid, one card per org, scoped to
 * plugin/script-execution usage only (storage/licensed-users belongs on the Billing page
 * instead, per Addison — a separate follow-up ticket, not this PR), with each card's trailing-
 * 12-month script-execution trend (bar chart, Spotter-usage-meter style — see
 * `UsageBarChart.tsx`) and a color tier (border/badge) from the same percentile cutoff every
 * card is judged against — see `computeUsageTiers` (backend). No section split by tier; color is
 * what makes high-usage orgs stand out in the single grid. Cards sort high → low by
 * trailing-12-month volume, and every bar chart shares one linear y-axis (`domainMax`, the
 * fleet-wide single-month high) so relative usage across cards is comparable at a glance;
 * current-month and peak-month counts are printed under the chart rather than needing a hover.
 *
 * Edit/create flow is untouched — this is the list view only. Search stays (Fuse.js), corrected
 * to search real fields (the old config searched a non-existent "organizations" key).
 */

const TIER_TONE: Record<OrganizationUsageListItem["usageTier"], string> = {
  high: "red",
  mid: "blue",
  low: "dark",
};

const TIER_LABEL: Record<OrganizationUsageListItem["usageTier"], string> = {
  high: "High usage",
  mid: "Typical usage",
  low: "Low usage",
};

const Tag = styled.span`
  display: block;
  width: fit-content;
  max-width: 100%;
  overflow-wrap: anywhere;
  padding: 2px 5px;
  border-radius: 2px;
  background-color: var(--dark400);
  color: var(--gray100);
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
`;

const StaleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  border-radius: 2px;
  background-color: var(--red100);
  color: var(--gray500);
  font-size: 11px;
  flex-shrink: 0;
`;

const Eyebrow = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gray100);
  margin-bottom: 12px;
`;

const StatStrip = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const StatChip = styled.div`
  background-color: var(--dark100);
  border: 1px solid var(--dark300);
  border-radius: 10px;
  padding: 12px 18px;
  min-width: 140px;
`;

const StatCount = styled.div<{ $tone: string }>`
  font-size: 28px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
  color: var(--${(p) => p.$tone}400);
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: var(--gray100);
  margin-top: 2px;
`;

const EmptyNote = styled.div`
  color: var(--gray100);
  font-size: 13px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
`;

const Card = styled(Link)<{ $tone: string }>`
  display: block;
  background-color: var(--dark110);
  border: 1px solid var(--dark300);
  border-left: 3px solid var(--${(p) => p.$tone}400);
  border-radius: 8px;
  padding: 14px 16px;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: var(--${(p) => p.$tone}300);
  }
`;

const OrgNameBlock = styled.div`
  min-width: 0;
`;

const OrgNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-bottom: 6px;
`;

const OrgName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--gray400);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const TierBadge = styled.span<{ $tone: string }>`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--${(p) => p.$tone}400);
  white-space: nowrap;
`;

const BarWrap = styled.div`
  margin: 10px 0 4px;
  width: 100%;
`;

const BarStats = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: var(--gray100);
`;

function AuthBadge(props: { auth?: { valid: boolean; error?: string } }) {
  const { auth } = props;
  if (!auth || auth.valid) {
    return null;
  }
  return (
    <Tooltip label={auth.error ?? "Authentication failed"} type="danger">
      <StaleBadge>
        <AlertTriangle size={12} />
        Stale token
      </StaleBadge>
    </Tooltip>
  );
}

const ConfigureOrganizationListScreen = () => {
  const { addComponentsJob } = useUpdateComponentsJob();
  const updateAllMutation = useMutation({
    mutationFn: () =>
      ApiService.fetch<{ scriptExecutionId: string; systemDomainId: string }>(
        "POST",
        `/api/plugin_instance/update_all`
      ),
    onSuccess: (data) =>
      addComponentsJob(
        data.scriptExecutionId,
        data.systemDomainId,
        "Updating components"
      ),
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["organizationList"],
    queryFn: async (): Promise<OrganizationUsageListItem[]> => {
      const response = await ApiService.fetch<{
        data: OrganizationUsageListItem[];
      }>("GET", `/api/config/organization`);
      return response.data;
    },
  });
  const { data: authStatus } = useOrganizationAuthCheck();

  const [search, setSearch] = useState("");

  // Highest single month across every org (not just the filtered/visible set) — the sparklines'
  // shared y-axis ceiling, so a card's scale doesn't shift as the search narrows the grid.
  const domainMax = useMemo(
    () => Math.max(1, ...(data ?? []).flatMap((org) => org.counts)),
    [data]
  );

  const totalVolume = (org: OrganizationUsageListItem) =>
    org.counts.reduce((sum, count) => sum + count, 0);

  const rows = useMemo(() => {
    const all = data ?? [];
    const filtered = search
      ? new Fuse<OrganizationUsageListItem>(all, {
          keys: ["title", "systemDomainId"],
          ignoreLocation: true,
          threshold: 0.2,
          shouldSort: false,
        })
          .search(search)
          .map((result) => result.item)
      : all;
    return [...filtered].sort((a, b) => totalVolume(b) - totalVolume(a));
  }, [data, search]);

  const highCount = useMemo(
    () => rows.filter((org) => org.usageTier === "high").length,
    [rows]
  );
  const lowCount = useMemo(
    () => rows.filter((org) => org.usageTier === "low").length,
    [rows]
  );

  return (
    <div>
      <PageTitle
        title="Organizations"
        error={isError ? "Problem loading organizations" : undefined}
      >
        <Button
          label="Update All Organizations"
          icon={RotateCcw}
          type="default"
          borderless
          onClick={() => updateAllMutation.mutate()}
          loading={updateAllMutation.isLoading}
        />
        <Button
          label="Add Organization"
          icon={Plus}
          link="/config/organization/new"
          type="success"
        />
        <Input
          value={search}
          onChange={setSearch}
          placeholder="Search"
          icon={Search}
          clearable
          width={200}
        />
      </PageTitle>

      {isLoading ? (
        <Spinner />
      ) : (
        !isError && (
          <PageWrapper>
            <StatStrip>
              <StatChip>
                <StatCount $tone="gray">{rows.length}</StatCount>
                <StatLabel>Organizations</StatLabel>
              </StatChip>
              <StatChip>
                <StatCount $tone="red">{highCount}</StatCount>
                <StatLabel>High usage</StatLabel>
              </StatChip>
              <StatChip>
                <StatCount $tone="gray">{lowCount}</StatCount>
                <StatLabel>Low usage</StatLabel>
              </StatChip>
            </StatStrip>

            <Eyebrow>Organizations</Eyebrow>
            {rows.length === 0 ? (
              <EmptyNote>No organizations match.</EmptyNote>
            ) : (
              <CardGrid>
                {rows.map((org) => (
                  <Card
                    key={org.systemDomainId}
                    to={org.systemDomainId}
                    $tone={TIER_TONE[org.usageTier]}
                    title={TIER_LABEL[org.usageTier]}
                  >
                    <OrgNameBlock>
                      <OrgNameRow>
                        <OrgName title={org.title}>{org.title}</OrgName>
                        {org.usageTier === "high" && (
                          <TierBadge $tone="red">{TIER_LABEL.high}</TierBadge>
                        )}
                        <AuthBadge auth={authStatus?.[org.systemDomainId]} />
                      </OrgNameRow>
                      <Tag title={org.systemDomainId}>{org.systemDomainId}</Tag>
                    </OrgNameBlock>

                    <BarWrap>
                      <UsageBarChart
                        values={org.counts}
                        tone={TIER_TONE[org.usageTier]}
                        domainMax={domainMax}
                      />
                    </BarWrap>
                    <BarStats>
                      <span>
                        {formatNumber(org.counts[org.counts.length - 1]) ?? "0"}{" "}
                        this month
                      </span>
                      <span>
                        max {formatNumber(Math.max(...org.counts)) ?? "0"}
                      </span>
                    </BarStats>
                  </Card>
                ))}
              </CardGrid>
            )}
          </PageWrapper>
        )
      )}
    </div>
  );
};

export default ConfigureOrganizationListScreen;
